// dashboard/src/pages/dashboard/ApiKeysPage.tsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from '../../hooks/useApiKeys';
import ApiKeysList from '../../components/dashboard/api-keys/ApiKeysList';
import CreateApiKeyModal from '../../components/dashboard/api-keys/CreateApiKeyModal';
import RevokeKeyConfirmation from '../../components/dashboard/api-keys/RevokeKeyConfirmation';
import WelcomeToApiKeysCard from '../../components/api-keys/WelcomeToApiKeysCard';

const ApiKeysPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [showWelcome, setShowWelcome] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { data: apiKeys = [], isLoading } = useApiKeys();
  const createApiKeyMutation = useCreateApiKey();
  const revokeApiKeyMutation = useRevokeApiKey();

  // Check if coming from subscription success
  useEffect(() => {
    if (location.state?.showWelcome && location.state?.fromSubscription) {
      setShowWelcome(true);
      // Clear the state to prevent showing again on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Show welcome if user has no API keys
  useEffect(() => {
    if (!isLoading && apiKeys.length === 0) {
      setShowWelcome(true);
    }
  }, [apiKeys, isLoading]);
  
  const handleCreateApiKey = async (keyName: string) => {
    const result = await createApiKeyMutation.mutateAsync({ keyName });
    setShowWelcome(false); // Hide welcome after creating first key

    // Navigate to playground after creating first API key
    setTimeout(() => {
      navigate('/dashboard/api-playground', {
        state: {
          showWelcome: true,
          fromApiKeys: true,
          newApiKey: true
        }
      });
    }, 2000); // Give user time to see the success

    return result;
  };

  const handleCreateFirstKey = () => {
    setIsCreateModalOpen(true);
  };
  
  const handleRevokeClick = (keyId: string) => {
    setSelectedKeyId(keyId);
    setIsRevokeModalOpen(true);
  };
  
  const handleRevokeConfirm = async () => {
    if (selectedKeyId) {
      await revokeApiKeyMutation.mutateAsync({ keyId: selectedKeyId });
      setIsRevokeModalOpen(false);
      setSelectedKeyId(null);
    }
  };
  
  return (
    <div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
          API Keys
        </h1>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <button
            type="button"
            onClick={() => setIsCreateModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Create API Key
          </button>
        </div>
      </div>
      
      <div className="mt-6">
        {/* Welcome Card for new users */}
        {showWelcome && (
          <WelcomeToApiKeysCard onCreateFirstKey={handleCreateFirstKey} />
        )}

        <p className="text-sm text-gray-500 mb-4">
          API keys allow you to authenticate requests to the CrawlyCarl API. Keep your API keys secure and do not share them in public areas such as GitHub or client-side code.
        </p>

        <ApiKeysList
          apiKeys={apiKeys}
          onRevoke={handleRevokeClick}
          isLoading={isLoading}
        />
      </div>
      
      <CreateApiKeyModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateApiKey={handleCreateApiKey}
        isLoading={createApiKeyMutation.isPending}
      />
      
      <RevokeKeyConfirmation
        isOpen={isRevokeModalOpen}
        onClose={() => setIsRevokeModalOpen(false)}
        onConfirm={handleRevokeConfirm}
        isLoading={revokeApiKeyMutation.isPending}
      />
    </div>
  );
};

export default ApiKeysPage;
